import Store from './store';
import { goodsTypesIcons, displayDateFormatter, statuses } from '@/core';

Store.registerModule('draggable', {
	namespaced: true,
	state: { item: null, isDragging: false },
	getters: { isDragging: ({ isDragging }) => isDragging, payload: ({ item }) => item && 'data' in item ? item.data : (item || []) },
	mutations: {
		drop: (state, payload = null) => state.item = payload,
		dragging: (state, value) => state.isDragging = value
	}
});

/**  Draggable and droppable is implemented as a directive. The droppable part is handled by recieving a store
  *  mutation which a single subscriber is handling. The subscriber is then responsible for looking up in a dictionary structure of bound unique droppable targets.
  *  This is controlled through the directive and a callback is expected. The directive takes the form: v-droppable:id="dropHandlerFunction"
  **/

let DragDrop = {};

function truncate(str) {
	let n = 12;
	if (str && str.length - 1 > n + 3) {
		return str.split('').reduce((acc, val, i) => {
			if (i < n) {
				return acc + val;
			} else if (i < n + 3) {
				return `${acc}.`;
			} else {
				return acc;
			}
		}, '');
	} else {
		return str;
	}
}

let TDraggablePlaceholder = {
	name: 'TDraggablePlaceholder',
	props: {
		selected: {
			type: Array,
			required: true,
			default() {
				return [];
			}
		},
	},
	data() {
		return {
			x: 0,
			y: 0,
			visible: false,
			item: null
		};
	},
	computed: {
		position() {
			return `left: ${this.x}px; top: ${this.y}px;`;
		},
		styleString() {
			return `position: absolute; top: ${this.selected.length > 1 ? 15 : 5}px; padding-right: 10px; display: flex; flex-direction: row; align-items: center; padding: 4px; z-index: 9999999;`;
		},
	},
	mounted() {
		window.addEventListener('mousemove', this.setXandY);
		this.$store.subscribe((mutation) => {
			if (mutation.type === 'draggable/dragging') {
				this.visible = mutation.payload;
				if (this.visible === false) {
					document.body.style.userSelect = '';
					this.item = null;
				} else {
					document.body.style.userSelect = 'none';
					this.item = this.selected[0];
				}
			}
		});
	},
	beforeDestroy() {
		window.removeEventListener('mousemove', this.setXandY);
	},
	methods: {
		setXandY(e) {
			this.x = e.clientX;
			this.y = e.clientY;
		},
		goodsTypesIcons(goodsType) { return goodsTypesIcons(goodsType); },
		getOrderIconColor(status) {
			return statuses.color(status);
		},
	},
	render() {
		/* eslint-disable */
		return (this.item && this.visible) ? (
			<mounting-portal
				mountTo="#dragdrop-portal"
				append
				multiple
			>
				<div class="draggable-card" style={this.position}>
					{this.selected > 1 ? <div class="empty-card" /> : null}
					<div style={this.styleString}>
						<TCheckbox checked={true} />
						<TTimpexIcon style="padding-left: 10px;" icon={this.goodsTypesIcons(this.item.goodsType)} color={this.getOrderIconColor(this.item.status)} />
						<span style="padding-left: 20px;">{ displayDateFormatter(this.item.etd.date) }</span>
						<TDetailsItem style="padding-left: 10px;" primary={truncate(this.item.despatchParty.name)} secondary={truncate(this.item.despatchParty.street)} />
						<TDetailsItem style="padding-left: 10px;" primary={truncate(this.item.deliveryParty.name)} secondary={truncate(this.item.deliveryParty.street)} />
					</div>
				</div>
			</mounting-portal>
		) : null;
	}
};

let TDraggable = {
	name: 'TDraggable',
	props: {
		context: {
			type: Object,
			required: true
		},
		item: { type: [ Object, String ] },
		placeholder: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return { target: null, pos: 0, distance: 0, isDragging: false, selected: [] };
	},
	computed: {
		data: {
			cache: false,
			get() {
				return this.item || 'getSelectedItems' in this.$parent ? this.$parent.getSelectedItems() : null;
			}
		}
	},
	beforeDestroy() {
		  window.removeEventListener('mouseup', this.drop);
		  this.context.$el.removeEventListener('mousedown', this.drag);
	},
	mounted() {
		window.addEventListener('mouseup', this.drop);
		this.context.$el.addEventListener('mousedown', this.drag);
	},
	methods: {
		async resetDragging() {
			window.removeEventListener('mousemove', this.move);
			document.body.style.userSelect = '';
			document.body.style.cursor = '';
			this.$store.commit('draggable/dragging', false);
			this.isDragging = false;
		},
		drop() {
			if (this.data && this.data.length && this.isDragging) {
				if (Math.abs(this.distance) > 10) {
					this.$store.commit('draggable/drop', this.selected);
				}
				this.resetDragging();
			}
		},
		drag(e) {
			if (this.data && this.data.length) {
				this.pos = e.clientX;
				this.target = this.context.$el;
				this.selected = this.context.getSelectedItems();
				document.body.style.cursor = 'grab';
				window.addEventListener('mousemove', this.move);
			}
		},
		move(e) {
			if ((e.buttons & 1) === 0) {
				this.resetDragging();
				return;
			}

			this.distance = this.pos - e.clientX;
			this.target = e.target;
			if (!this.isDragging && Math.abs(this.distance) > 100) {
				this.$store.commit('draggable/dragging', true);
				this.isDragging = true;
			}
		}
	},
	render() {
		return this.placeholder ? <TDraggablePlaceholder selected={this.selected} /> : null;
	}
};

let droppable = {};
DragDrop.install = function(Vue) {
	let el;
	document.head.appendChild((
		el = document.createElement('style'),
		el.innerHTML = `
			.draggable-card {
				width: 450px;
				height: 50px;
				box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.25), inset 0 -1px 0 0 #edf0f2;
				border: solid 1px #1070ca;
				background-color: #dbeaf7;
				position: fixed;
				z-index: 99999999;
				will-change: transform;
				pointer-events: none;
				font-size: 12px;
				font-family: "SF UI Text", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
			}

			.empty-card {
				width: 450px;
				height: 50px;
				border: solid 1px #1070ca;
				box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.2);
				background-color: #dbeaf7;
				will-change: transform;
				transform: translate(-10px, 10px);
			}
		`,
		el
	))

	let dropTarget = null;
	let notSubscribed = true;
	let secretKey = Symbol('droppable');

	function setDropTarget(e) {
		dropTarget = e.target;
	}

	window.addEventListener('mouseup', setDropTarget);

	Vue.component('TDraggablePlaceholder', TDraggablePlaceholder);
	Vue.component('TDraggable', TDraggable);

	Vue.mixin({
		created() {
			if (this.$store && 'subscribe' in this.$store && notSubscribed) {
				this[secretKey] = this.$store.subscribe((mutation) => {
					if (mutation.type === 'draggable/drop') {
						let hasFoundTarget = false;
						for (let { el, value } of Object.values(droppable)) {
							let target = dropTarget;
							while (target && (target !== document.body)) {
								if (target === el) {
									hasFoundTarget = true;
									break;
								}
								target = target.parentNode;
							}

							if (hasFoundTarget) {
								value(mutation.payload);
								hasFoundTarget = false;
								break;
							}
						}
					}
				});
				notSubscribed = false;
			}
		},
		beforeDestroy() {
			if (this[secretKey]) {
				try {
					this[secretKey].unsubscribe();
					notSubscribed = true;
				} catch (err) {
					console.warn('Plugin might be outdatet or needs revision.');
				}
			}
		}
	});

	Vue.directive('droppable', {
		bind(el, { value, arg }) {
			if (!arg) throw Error('Argument must be set to provide a droppable target.');
			if (!value) throw Error('Value must be set as a callback function.');
			if (value && !(value instanceof Function)) throw Error('Value must be a function');
			droppable[arg] = { el, value };
		},
		unbind(el, { arg }) {
			if (!arg) return;
			delete droppable[arg];
		}
	});
};

export default DragDrop;

// Add element as droppable when unable to use directive
export function addAsDroppable(el, id, value) {
	if (!id) throw Error('Argument must be set to provide a droppable target.');
	if (!value) throw Error('Value must be set as a callback function.');
	if (value && !(value instanceof Function)) throw Error('Value must be a function');
	droppable[id] = { el, value };
}

export function removeAsDroppable(id) {
	if (!id) return;
	delete droppable[id];
}